import { useState } from 'react';
import { onePagersService } from '../services/onePagersService';

interface SendEmailModalProps {
  accountId: string;
  introductionId: string;
  versionId: string;
  conciergeId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseRecipients(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function SendEmailModal({
  accountId,
  introductionId,
  versionId,
  conciergeId,
  onClose,
  onSuccess,
}: SendEmailModalProps) {
  // TODO: pre-populate with PE client contacts once surfaced from Salesforce
  const [recipients, setRecipients] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);

  const handleSend = async () => {
    setRecipientsError(null);
    setError(null);

    const parsed = parseRecipients(recipients);
    if (parsed.length === 0) {
      setRecipientsError('At least one recipient is required.');
      return;
    }
    const invalid = parsed.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length > 0) {
      setRecipientsError(
        `Invalid email address${invalid.length > 1 ? 'es' : ''}: ${invalid.join(', ')}`
      );
      return;
    }

    setLoading(true);
    try {
      await onePagersService.sendEmail(accountId, introductionId, versionId, {
        recipients: parsed,
        conciergeId,
        body: body.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative mx-auto mt-24 w-full max-w-[560px] rounded-md bg-white p-6 shadow-lg">
        <div className="text-base font-semibold text-gray-900">Send One-Pager Email</div>
        <p className="mt-2 text-sm text-gray-600">
          Recipients will get a project update email with this one-pager as a PDF version as well
          as a direct link to view it in their platform.
        </p>

        <div className="mt-4 flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
          <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          A PDF of this one-pager will be included in the email.
        </div>

        <div className="mt-5">
          <label className="block text-sm font-medium text-gray-700">
            Recipients <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              recipientsError ? 'border-red-400' : 'border-[rgba(0,0,0,0.15)]'
            }`}
            placeholder="email@example.com, another@example.com"
            value={recipients}
            onChange={(e) => {
              setRecipients(e.target.value);
              setRecipientsError(null);
            }}
            disabled={loading}
          />
          {recipientsError && (
            <p className="mt-1 text-xs text-red-600">{recipientsError}</p>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">Email Body</label>
          <textarea
            className="mt-1 w-full rounded-md border border-[rgba(0,0,0,0.15)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
            placeholder="Type body content here"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={loading}
          />
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="h-[32px] rounded-[8px] bg-white px-[13px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] border border-[rgba(0,0,0,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="h-[32px] rounded-[8px] bg-[#29446D] px-[24px] text-center font-['Inter',sans-serif] text-[14px] font-bold leading-[20px] tracking-[-0.1504px] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSend}
            disabled={loading}
          >
            {loading ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
